import { BaseApiService } from './baseApi';
import type { EducationItem, ApiResponse } from '../../../../types';

export interface CreateEducationRequest {
  title_fr: string;
  title_en: string;
  institution_fr: string;
  institution_en: string;
  description_fr?: string;
  description_en?: string;
  year: string;
  level_fr?: string;
  level_en?: string;
  certification_url?: string;
  rncp_code?: string;
  icon_type?: string;
  color?: string;
  image_url?: string;
  order_index?: number;
  is_visible?: boolean;
}

export class EducationApiService extends BaseApiService {
  constructor() {
    super('/education');
  }

  /**
   * Fetch toutes les formations
   */
  async getAll(): Promise<ApiResponse<EducationItem[]>> {
    return this.requestGet<EducationItem[]>('', false);
  }

  /**
   * Fetch les formations visibles uniquement
   */
  async getVisible(): Promise<ApiResponse<EducationItem[]>> {
    return this.requestGet<EducationItem[]>('/visible', false);
  }

  /**
   * Fetch une formation par ID
   */
  async getById(id: string): Promise<ApiResponse<EducationItem>> {
    return this.requestGet<EducationItem>(`/${id}`, false);
  }

  /**
   * Fetch les formations par section
   */
  async getBySection(sectionId: string): Promise<ApiResponse<EducationItem[]>> {
    return this.requestGet<EducationItem[]>(`/section/${sectionId}`, false);
  }

  /**
   * Create une nouvelle formation
   */
  async create(data: CreateEducationRequest): Promise<ApiResponse<EducationItem>> {
    return this.requestPost<EducationItem>('', data);
  }

  /**
   * Update une formation
   */
  async update(id: string, data: Partial<CreateEducationRequest>): Promise<ApiResponse<EducationItem>> {
    return this.requestPut<EducationItem>(`/${id}`, data);
  }

  /**
   * Delete une formation par ID
   */
  async deleteById(id: string): Promise<ApiResponse<boolean>> {
    return this.requestDelete<boolean>(`/${id}`);
  }

  /**
   * Alias pour compatibilité
   */
  async deleteEducation(id: string): Promise<ApiResponse<boolean>> {
    return this.deleteById(id);
  }

  /**
   * Réorganiser les formations
   */
  async reorder(orders: { id: string; order_index: number }[]): Promise<ApiResponse<boolean>> {
    return this.requestPost<boolean>('/reorder', { educationOrders: orders });
  }

  /**
   * Toggle la visibilité d'une formation
   */
  async toggleVisibility(id: string): Promise<ApiResponse<EducationItem>> {
    return this.requestPatch<EducationItem>(`/${id}/visibility`);
  }

  /**
   * Valider l'intégrité des formations
   */
  async validate(): Promise<ApiResponse<any>> {
    return this.requestGet<any>('/validate');
  }

  /**
   * Corriger l'ordre des formations
   */
  async fixOrder(): Promise<ApiResponse<any>> {
    return this.requestPost<any>('/fix-order');
  }

  /**
   * Search des formations
   */
  async search(query: string, filters?: {
    year?: string;
    level?: string;
    institution?: string;
    visible?: boolean;
  }): Promise<ApiResponse<EducationItem[]>> {
    const params = new URLSearchParams();
    params.append('q', query);

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          params.append(key, String(value));
        }
      });
    }

    return this.requestGet<EducationItem[]>(`/search?${params.toString()}`, false);
  }
}

export const educationApi = new EducationApiService();
